/**
 * Attendance Log Query Functions
 * 
 * Fetches attendance logs with lesson details.
 * @module fehlzeiten/services/queries/attendanceLogQueries
 */

import { supabase } from '../../../../../lib/supabase';

export interface AttendanceLogWithLesson {
    status: string;
    lesson: {
        period: {
            start_time: string;
            end_time: string;
        } | null;
        subject: {
            name: string;
        } | null;
    } | null;
}

/**
 * Fetches attendance logs for a student on a specific date with lesson details.
 */
export async function fetchAttendanceLogsForDate(
    studentId: string,
    date: string
): Promise<AttendanceLogWithLesson[]> {
    const { data, error } = await supabase
        .from('student_attendance_logs' as never)
        .select(`
      status,
      lesson:lesson_id (
        period:period_id (start_time, end_time),
        subject:subject_id (name)
      )
    ` as never)
        .eq('student_id' as never, studentId as never)
        .gte('timestamp' as never, `${date}T00:00:00` as never)
        .lt('timestamp' as never, `${date}T23:59:59` as never);

    if (error) {
        console.error('[fetchAttendanceLogsForDate] Error:', error);
        return [];
    }

    return (data as unknown as AttendanceLogWithLesson[]) || [];
}

/**
 * Fetches the student_id from an absence entry by ID.
 */
export async function fetchStudentIdFromEntry(
    entryId: string
): Promise<string | null> {
    const { data, error } = await supabase
        .from('vw_react_student_absences' as never)
        .select('student_id' as never)
        .eq('id' as never, entryId as never)
        .maybeSingle();

    if (error || !data) return null;
    return (data as unknown as { student_id: string }).student_id;
}

/**
 * Fetches lesson notes from attendance logs for specific lessons.
 */
export async function fetchLessonNotes(
    studentId: string,
    lessonIds: string[]
): Promise<Record<string, string>> {
    if (lessonIds.length === 0) return {};

    const { data } = await supabase
        .from('student_attendance_logs' as never)
        .select('lesson_id, notes' as never)
        .eq('student_id' as never, studentId as never)
        .in('lesson_id' as never, lessonIds as never);

    const notesMap: Record<string, string> = {};
    if (Array.isArray(data)) {
        for (const log of data as unknown as Array<{ lesson_id: string; notes: string | null }>) {
            if (log.lesson_id && log.notes) {
                notesMap[log.lesson_id] = log.notes;
            }
        }
    }
    return notesMap;
}
